import React, { useState, useEffect } from "react";
import {
  X,
  FileText,
  Download,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  ExternalLink,
  Loader,
} from "lucide-react";
import "./app.css";

const FileViewer = ({ url, fileName, close }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [mode, setMode] = useState(null); // "embed" | "pdf" | "image" | "other"
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (!url) return;
    let objUrl = null;

    const driveId =
      url.match(/[?&]id=([^&]+)/)?.[1] || url.match(/\/d\/([^/]+)/)?.[1];

    if (driveId) {
      setBlobUrl(`https://drive.google.com/file/d/${driveId}/preview`);
      setMode("embed");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const type = blob.type || "";
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);

        if (type.includes("pdf")) setMode("pdf");
        else if (type.startsWith("image/")) setMode("image");
        else setMode("other");
      } catch (err) {
        console.error("FileViewer fetch:", err);
        setError(true);
      }
      setLoading(false);
    })();

    return () => {
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [url]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 300));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 25));
  const handleRotate = () => setRotation((r) => (r + 90) % 360);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = blobUrl || url;
    a.download = fileName || "plik";
    a.click();
  };

  const showZoom = mode === "pdf" || mode === "image" || mode === "embed";

  return (
    <div
      className="sn-overlay"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className="sn-modal fv-modal">
        <div className="sn-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <FileText size={18} />
            <div>
              <h2 className="sn-title">{fileName || "Podgląd pliku"}</h2>
            </div>
          </div>
          <button className="ld-close" onClick={close}>
            <X size={18} />
          </button>
        </div>

        <div className="fv-toolbar">
          {showZoom && (
            <>
              <button
                className="fv-tb-btn"
                onClick={handleZoomOut}
                title="Pomniejsz"
              >
                <ZoomOut size={14} />
              </button>
              <span className="fv-zoom-label">{zoom}%</span>
              <button
                className="fv-tb-btn"
                onClick={handleZoomIn}
                title="Powiększ"
              >
                <ZoomIn size={14} />
              </button>
              {mode === "image" && (
                <button
                  className="fv-tb-btn"
                  onClick={handleRotate}
                  title="Obróć"
                >
                  <RotateCw size={14} />
                </button>
              )}
              <button
                className="fv-tb-btn"
                onClick={handleReset}
                title="Resetuj widok"
              >
                <Maximize2 size={14} />
              </button>
              <span className="et-tb-sep" />
            </>
          )}
          <button
            className="fv-tb-btn"
            onClick={handleDownload}
            title="Pobierz"
          >
            <Download size={14} />
          </button>
          <a
            className="fv-tb-btn"
            href={url}
            target="_blank"
            rel="noreferrer"
            title="Otwórz w nowej karcie"
          >
            <ExternalLink size={14} />
          </a>
        </div>

        <div className="fv-content">
          {loading && (
            <div className="fv-loading">
              <Loader size={28} className="fv-spin" />
              <span>Ładowanie...</span>
            </div>
          )}

          {error && (
            <div className="fv-error">
              <FileText size={40} strokeWidth={1} />
              <p>Nie udało się załadować pliku</p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="lq-btn lq-btn--muted"
              >
                <ExternalLink size={14} /> Otwórz w przeglądarce
              </a>
            </div>
          )}

          {/* Google Drive embed */}
          {mode === "embed" && blobUrl && (
            <div
              className="fv-pdf-wrap"
              style={{ transform: `scale(${zoom / 100})` }}
            >
              <iframe
                src={blobUrl}
                className="fv-iframe"
                title="File viewer"
                allow="autoplay"
              />
            </div>
          )}

          {/* PDF blob */}
          {mode === "pdf" && blobUrl && (
            <div
              className="fv-pdf-wrap"
              style={{ transform: `scale(${zoom / 100})` }}
            >
              <iframe
                src={`${blobUrl}#toolbar=0`}
                className="fv-iframe"
                title="PDF viewer"
              />
            </div>
          )}

          {/* Image blob */}
          {mode === "image" && blobUrl && (
            <div className="fv-img-wrap">
              <img
                src={blobUrl}
                alt={fileName}
                className="fv-img"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                }}
              />
            </div>
          )}

          {/* Nieznany format */}
          {mode === "other" && (
            <div className="fv-error">
              <FileText size={40} strokeWidth={1} />
              <p>Podgląd niedostępny</p>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="lq-btn lq-btn--muted"
                  onClick={handleDownload}
                >
                  <Download size={14} /> Pobierz
                </button>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="lq-btn lq-btn--muted"
                >
                  <ExternalLink size={14} /> Otwórz w przeglądarce
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileViewer;
